import Vue from 'vue'
import VueRouter from 'vue-router'
import Login from '../views/Login.vue'
import HomeView from '../views/HomeView.vue'
import CronogramaView from '../views/CronogramaView.vue'
import DashboardView from '../views/DashboardView.vue'
import UsersView from '../views/UsersView.vue'
import ConfiguracaoScoreView from '../views/ConfiguracaoScoreView.vue'
import authService from '../services/authService'

Vue.use(VueRouter)

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: Login
  },
  {
    path: '/',
    redirect: '/hosts'
  },
  {
    path: '/hosts',
    name: 'Home',
    component: HomeView,
    meta: {
      requiresAuth: true,
      allowedRoles: ['admin', 'gestor']
    }
  },
  {
    path: '/cronogramas',
    name: 'Cronogramas',
    component: CronogramaView,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['admin', 'gestor']
    }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: DashboardView,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['user', 'admin', 'gestor']
    }
  },
  {
    path: '/usuarios',
    name: 'Usuarios',
    component: UsersView,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['admin'] // Apenas admin pode acessar gestão de usuários
    }
  },
  {
    path: '/configuracoes',
    name: 'ConfiguracaoScore',
    component: ConfiguracaoScoreView,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['admin'] // Apenas admin pode acessar configurações
    }
  }
]

const router = new VueRouter({
  mode: 'hash',
  base: process.env.BASE_URL,
  routes
})

router.beforeEach(async (to, from, next) => {
  console.log('🧭 Router Guard - Navegando de:', from.name, 'para:', to.name)
  
  // Detectar parâmetros especiais na URL (abordagem otimizada)
  // Priorizar captura direta do hash completo para maior precisão
  // 1. Primeiro verificar a URL completa diretamente (mais preciso)
  const fullHash = window.location.hash
  const normalizedHash = fullHash && fullHash.startsWith('#')
    ? fullHash.substring(1)
    : fullHash
  
  // NOVA LÓGICA: Salvar URLs que contenham /dashboard?logview= ou filtros=
  if (!to.path.includes('/login') && 
      (normalizedHash.includes('/dashboard?logview=') || 
       normalizedHash.includes('filtros=') ||
       (normalizedHash.includes('/dashboard') && normalizedHash.includes('?')))) {
    console.log('🔖 URL especial do dashboard detectada:', normalizedHash)
    // Salvar apenas o path/query (sem #) para facilitar o uso com o Vue Router
    localStorage.setItem('redirect_after_login', normalizedHash)
  } 
  // 2. Backup: verificar usando informações do router
  else if (to.path === '/dashboard' && to.query && 
          (to.query.logview || to.query.filtros || Object.keys(to.query).length > 0)) {
    const fullPath = to.fullPath
    console.log('🔖 URL especial do dashboard detectada (via router):', fullPath)
    localStorage.setItem('redirect_after_login', fullPath)
  }
  
  const isAuthenticated = authService.isAuthenticated()
  console.log('🔐 Status de autenticação:', isAuthenticated)
  
  // Se está indo para login e já está autenticado
  if (to.name === 'Login' && isAuthenticated) {
    console.log('🔄 Usuário autenticado tentando acessar login, validando token...')
    try {
      const isTokenValid = await authService.validateToken()
      console.log('🔑 Token válido:', isTokenValid)
      
      if (isTokenValid) {
        // Token válido, verificar se há redirecionamento pendente
        const redirectUrl = localStorage.getItem('redirect_after_login')
        if (redirectUrl) {
          console.log('🔀 Redirecionando para URL salva:', redirectUrl)
          localStorage.removeItem('redirect_after_login')
          // redirectUrl agora é sempre um path/query (sem #), compatível com o Vue Router
          next(redirectUrl)
          return
        }
        
        // Senão, redirecionar para a página apropriada baseada na role
        const user = authService.getUser()
        console.log('✅ Token válido, redirecionando baseado na role:', user.role)
        
        if (user.role === 'admin' || user.role === 'gestor') {
          next('/')
        } else if (user.role === 'user') {
          next('/dashboard')
        } else {
          console.log('❌ Role não reconhecida, permitindo acesso ao login')
          next()
        }
      } else {
        // Token inválido, permitir acesso ao login
        console.log('❌ Token inválido, permitindo acesso ao login')
        next()
      }
    } catch (error) {
      console.error('💥 Erro ao validar token no login:', error)
      // Em caso de erro, permitir acesso ao login
      next()
    }
    return
  }
  
  // Se está indo para login e não está autenticado
  if (to.name === 'Login') {
    console.log('🚪 Acesso ao login permitido (não autenticado)')
    next()
    return
  }
  
  // Se a rota requer autenticação
  if (to.matched.some(record => record.meta.requiresAuth)) {
    console.log('🔒 Rota requer autenticação')
    if (!isAuthenticated) {
      // Não está autenticado, redirecionar para login
      console.log('❌ Não autenticado, redirecionando para login')
      
      // Salvar a URL atual se for uma URL de dashboard com logview ou filtros
      if (to.path === '/dashboard' && to.query && 
          (to.query.logview || to.query.filtros || Object.keys(to.query).length > 0)) {
        console.log('📌 Salvando URL especial de dashboard para redirecionamento após login:', to.fullPath)
        localStorage.setItem('redirect_after_login', to.fullPath)
      } else {
        console.log('📌 URL atual não é uma URL especial de dashboard, não será salva:', to.fullPath)
      }
      
      next('/login')
    } else {
      // Está autenticado, mas vamos validar o token
      console.log('🔄 Autenticado, validando token...')
      try {
        const isTokenValid = await authService.validateToken()
        console.log('🔑 Token válido:', isTokenValid)
        
        if (isTokenValid) {
          // Token válido, verificar permissões de role
          const user = authService.getUser()
          const routeMeta = to.meta
          
          if (routeMeta.allowedRoles) {
            console.log('🎭 Verificando permissões de role:', { 
              userRole: user.role, 
              allowedRoles: routeMeta.allowedRoles 
            })
            
            // Verificação especial: gestor não pode acessar /usuarios
            if (to.path === '/usuarios' && user.role === 'gestor') {
              console.log('❌ Gestor não pode acessar gestão de usuários, redirecionando...')
              // Redirecionar para a última rota válida ou dashboard como padrão
              const previousRoute = from.path && from.path !== '/login' ? from.path : '/dashboard'
              next(previousRoute)
              return
            }
            
            if (routeMeta.allowedRoles.includes(user.role)) {
              console.log('✅ Usuário tem permissão para acessar a rota')
              next()
            } else {
              console.log('❌ Usuário não tem permissão para acessar a rota')
              
              // Redirecionar para a página apropriada baseada na role
              if (user.role === 'admin' || user.role === 'gestor') {
                next('/')
              } else if (user.role === 'user') {
                next('/dashboard')
              } else {
                next('/login')
              }
            }
          } else {
            console.log('✅ Rota sem restrições de role, permitindo acesso')
            next()
          }
        } else {
          // Token inválido ou expirado, redirecionar para login
          console.warn('❌ Token inválido ou expirado, redirecionando para login')
          
          // Salvar a URL atual se for uma URL de dashboard com logview ou filtros
          if (to.path === '/dashboard' && to.query && 
              (to.query.logview || to.query.filtros || Object.keys(to.query).length > 0)) {
            console.log('📌 Salvando URL especial de dashboard para redirecionamento após login:', to.fullPath)
            localStorage.setItem('redirect_after_login', to.fullPath)
          } else {
            console.log('📌 URL atual não é uma URL especial de dashboard, não será salva:', to.fullPath)
          }
          
          next('/login')
        }
      } catch (error) {
        console.error('💥 Erro ao validar token:', error)
        // Em caso de erro, redirecionar para login por segurança
        
        // Salvar a URL atual se for uma URL de dashboard com logview ou filtros
        if (to.path === '/dashboard' && to.query && 
            (to.query.logview || to.query.filtros || Object.keys(to.query).length > 0)) {
          console.log('📌 Salvando URL especial de dashboard para redirecionamento após login:', to.fullPath)
          localStorage.setItem('redirect_after_login', to.fullPath)
        } else {
          console.log('📌 URL atual não é uma URL especial de dashboard, não será salva:', to.fullPath)
        }
        
        next('/login')
      }
    }
  } else {
    // Rota não requer autenticação
    console.log('🌐 Rota não requer autenticação, permitindo acesso')
    next()
  }
})

export default router 